/**************************************************************************************************/
/*   File: /src/app/stores/effects/creation/effects.ts                                              */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Tue Apr 23 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { Observable, of, empty } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom, filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';
import * as fromSelectors from '../../selectors';
import { Store } from '@ngrx/store';
import { getSelectedPlan } from '../../selectors/details';
import { DossierContent } from '../../../shared/models';

/**
 * The creation effects, all the action that dispatch a call to service
 */
@Injectable()
export class CreationEffects {
  /**
   * The select plan effect.
   * When a plan is set reload ambiti by plancode
   */
  @Effect()
  selectPlanEffect$ = this.actions$.pipe(
    ofType(fromActions.fromCreation.DossierCreationActionsType.SELECT_PLAN_TYPE),
    map((action: fromActions.fromCreation.SelectPlanType) =>
      new fromActions.fromCreation.LoadAmbitiByPlan({
        planCode: action.payload.planCode
      })
    )
  );
  /**
   * The create dossier effect.
   * If the creation goes well then dispatch create dossier success action else
   * dispatch create dossier fail
   */
  @Effect()
  createDossierEffect$ = this.actions$.pipe(
    ofType<fromActions.fromCreation.CreateDossier>(
      fromActions.fromCreation.DossierCreationActionsType.CREATE_DOSSIER
    ),
    switchMap((action: fromActions.fromCreation.CreateDossier) =>
      this.dossierService.create(action.payload.dossierContent).pipe(
        mergeMap((dossierId) =>
          [
            new fromActions.fromCreation.CreateDossierSuccess({
              dossierId: dossierId
            })
          ]
        ),
        catchError(() =>
          of(
            new fromActions.fromCreation.CreateDossierFail({
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.CANNOT_CREATE_DOSSIER,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromCreation.STATE_NAME
              )
            })
          )
        )
      )
    )
  );
  /**
   * The create dossier success effect.
   * This dispatch the action to go to the details page
   */
  @Effect()
  createDossierSuccessEffect$ = this.actions$.pipe(
    ofType<fromActions.fromCreation.CreateDossierSuccess>(
      fromActions.fromCreation.DossierCreationActionsType.CREATE_DOSSIER_SUCCESS
    ),
    map((action: fromActions.fromCreation.CreateDossierSuccess) =>
      new fromActions.fromRouter.Go(
        {
          path: [
            'shell',
            'details',
            action.payload.dossierId.id
          ]
        }
      )
    )
  );
  /**
   * Load ambiti by plan
   */
  @Effect()
  loadAmbitiByPlanEffect$ = this.actions$.pipe(
    ofType<fromActions.fromCreation.LoadAmbitiByPlan>(
      fromActions.fromCreation.DossierCreationActionsType.LOAD_AMBITI_BY_PLAN
    ),
    switchMap((action) =>
      this.ambitiService.getAllAmbitiByPlanCode(action.payload.planCode).pipe(
        map(ambiti =>
          new fromActions.fromCreation.LoadAmbitiByPlanSuccess(
            {
              ambiti: ambiti
            }
          )
        ),
        catchError((fail) =>
          of(
            new fromActions.fromCreation.LoadAmbitiByPlanFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.AMBITI_FAIL,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromCreation.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
    * Reload summaries on language changed
    */
  @Effect()
  reloadDossiersEffect$ = this.actions$.pipe(
    ofType<fromActions.fromUi.LanguageChangeSuccessAction>(
      fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_SUCCESS_ACTION
    ),
    // tslint:disable-next-line
    withLatestFrom(this.store, (action, state) => state.creation.dossierContent),
    filter(dossierContent => dossierContent !== undefined),
    // withLatestFrom(this.store.select(fromSelectors.fromCreation.getDossierContent)),
    map(dossierContent =>
      new fromActions.fromCreation.LoadAmbitiByPlan({
        planCode: dossierContent.planCode
      })
    )
  );

  /**
   * The contructor
   * @param {Actions} actions$ The actions
   * @param {fromShared.services.DossierService} dossierService The service to interact with middleware dossier
   */
  constructor(
    private store: Store<fromStates.fromRoot.RootState>,
    private actions$: Actions,
    private dossierService: fromShared.services.DossierService,
    private ambitiService: fromShared.services.AmbitiService
  ) { }

}
